import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { langFromPath, localizePath } from '../../lib/lang';
// T81 — consultant mode (顾问模式). TYPE-ONLY imports from the T80 palette core: erased at
// build, so the consultant atlas is NOT pulled into the public/eager bundle. The runtime
// helpers (resolveRoute/tr/pathwayState) are loaded via a DYNAMIC import only once a
// verified consultant surface is active — the atlas stays admin-only, like the T80 palette.
import type { AtlasRecipe } from './admin-case/commandPaletteCore';
type PaletteCore = typeof import('./admin-case/commandPaletteCore');

// Cloudflare Turnstile (bot protection). The site key is public; the secret lives
// only in the chat-assistant edge function (TURNSTILE_SECRET_KEY). Enforcement is
// on only once that secret is set server-side.
const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_PUBLIC_TURNSTILE_SITE_KEY as string) || '0x4AAAAAADrkrFEFD6AO16Om';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      execute: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

type Role = 'user' | 'assistant';

interface ChatMessage {
  role: Role;
  content: string;
  // T81 — consultant mode: an assistant turn may carry the atlas recipe ids the edge
  // function selected (verified consultants only). The frontend renders each as a 带我去
  // button below the message. Absent on every public turn.
  recipeIds?: string[];
}

// Self-contained bilingual strings (mirrors the user's UI language). The
// assistant's own replies follow the language the user types in the chat.
const STRINGS = {
  en: {
    open: 'Chat with us',
    title: 'Hank · Think Forward',
    subtitle: 'General immigration questions',
    greeting:
      "Hi! 👋 I'm Hank, the Think Forward assistant. Ask me general questions about Canadian " +
      'immigration — Express Entry, PNP, work/study permits, family sponsorship, LMIA, and more. ' +
      'For advice on your specific case, I can help you book a consultation with our licensed RCIC.',
    placeholder: 'Type your question…',
    send: 'Send',
    close: 'Close',
    book: 'Book a CA$99 consultation',
    error:
      'Sorry, something went wrong. Please try again, or book a consultation to speak with our RCIC.',
    disclaimer: 'General info only — not legal advice.',
    consultantSubtitle: 'Consultant mode · in-app navigation',
    consultantBadge: 'Consultant mode',
    goto: 'Take me there',
    demo: 'Show me',
    needsCase: 'Open a case first',
    notActive: 'Not yet active',
  },
  zh: {
    open: '在线咨询',
    title: 'Hank · Think Forward',
    subtitle: '加拿大移民常见问题',
    greeting:
      '你好！👋 我是 Think Forward 的在线助手 Hank。你可以问我关于加拿大移民的常见问题——快速通道（Express Entry）、' +
      '省提名（PNP）、工作/学习许可、家庭团聚、LMIA 等。如需针对你个人情况的建议，我可以帮你预约持牌移民顾问（RCIC）咨询。',
    placeholder: '输入你的问题…',
    send: '发送',
    close: '关闭',
    book: '预约 CA$99 咨询',
    error: '抱歉，出错了。请重试，或预约咨询与我们的持牌顾问（RCIC）沟通。',
    disclaimer: '仅供一般参考，非法律意见。',
    consultantSubtitle: '顾问模式 · 应用内导航',
    consultantBadge: '顾问模式',
    goto: '带我去',
    demo: '演示',
    needsCase: '打开某个案件后可用',
    notActive: '尚未启用',
  },
};

// Pages on our own site — rendered as one-tap in-app links (SPA navigation) so a
// customer never has to copy/paste a URL. We also extract the path from any full
// URL (even a wrong/hallucinated domain like thinkforwardimmigration.com) and
// route internally when the path matches, so a click always lands on the right page.
const INTERNAL_ROUTES = new Set([
  '/', '/about', '/services', '/pricing', '/testimonials', '/faq',
  '/policy-updates', '/pathway-finder', '/contact', '/book-consultation',
]);

// [T164 · G1] Only these official government / regulator domains (and their real
// subdomains) may render as a tappable EXTERNAL link. Every other URL an assistant
// reply produces — even a full https:// one — degrades to plain text (never a clickable
// <a>), so an injected reply can't ship a trustworthy-labelled link to an arbitrary /
// phishing domain. The system prompt already restricts Hank to canada.ca; this is the
// code tooth behind that instruction. (Internal SPA routes are unaffected — resolved above.)
const ALLOWED_EXTERNAL_DOMAINS = ['canada.ca', 'college-ic.ca'] as const;

function allowedBaseDomain(hostname: string): string | null {
  const h = hostname.toLowerCase();
  for (const base of ALLOWED_EXTERNAL_DOMAINS) {
    // Exact apex or a real subdomain — the leading dot stops 'evilcanada.ca' /
    // 'canada.ca.evil.com' from matching.
    if (h === base || h.endsWith('.' + base)) return base;
  }
  return null;
}

export function resolveLink(raw: string): { internal?: string; external?: string; host?: string } {
  const isUrl = /^https?:\/\//i.test(raw);
  let pathname = raw;
  let base: string | null = null;
  if (isUrl) {
    try {
      const u = new URL(raw);
      pathname = u.pathname;
      base = allowedBaseDomain(u.hostname);
    } catch {
      return {}; // unparseable URL → plain text (never a clickable link)
    }
  }
  // An allowlisted official domain is ALWAYS an external link — checked BEFORE the
  // internal-route map so its root path '/' is never remapped to our home page.
  // (renderInline appends a real-domain note to a non-URL label.)
  if (isUrl && base) return { external: raw, host: base };
  // A path that matches one of our SPA routes → internal navigation. This also fires
  // for a full URL on a wrong/hallucinated domain (e.g. thinkforwardimmigration.com/…):
  // the foreign domain is discarded and the click lands on the right in-app page —
  // never a clickable link to a non-allowlisted domain.
  const clean = pathname.replace(/\/+$/, '') || '/';
  if (INTERNAL_ROUTES.has(clean)) return { internal: clean };
  return {};
}

// [T333] In-app navigation out of the chat keeps a zh reader in /zh. Hank's
// replies and the route map above name the CANONICAL (English) paths; the
// language the visitor is reading in is the URL's, so the prefix is decided by
// the CURRENT pathname, not by the link. Portal/admin surfaces have no /zh
// variant and are never under it, so they pass through unchanged. Pure, so the
// zh-nav test drives it directly (no jsdom in the repo).
export function localizeInternalPath(path: string, currentPathname: string): string {
  return localizePath(path, langFromPath(currentPathname));
}

// Friendly, bilingual labels so an in-chat link reads like a tappable option
// ("免费路径评估 →") instead of a raw URL path ("/pathway-finder").
const ROUTE_LABELS: Record<string, { en: string; zh: string }> = {
  '/': { en: 'Home', zh: '首页' },
  '/about': { en: 'About us', zh: '关于我们' },
  '/services': { en: 'Our services', zh: '服务介绍' },
  '/pricing': { en: 'Pricing', zh: '收费标准' },
  '/testimonials': { en: 'Client reviews', zh: '客户评价' },
  '/faq': { en: 'FAQ', zh: '常见问题' },
  '/policy-updates': { en: 'Policy Updates', zh: '政策更新' },
  '/pathway-finder': { en: 'Pathway Finder', zh: '免费路径评估' },
  '/contact': { en: 'Contact us', zh: '联系我们' },
  '/book-consultation': { en: 'Book a consultation', zh: '预约咨询' },
};

function friendlyLabel(path: string, lang: 'en' | 'zh'): string {
  return ROUTE_LABELS[path] ? ROUTE_LABELS[path][lang] : path;
}

// Turn an assistant message into rich nodes: **bold**, [label](target) links,
// bare URLs, and bare internal paths (/pathway-finder, …) all become clickable.
export function renderInline(
  text: string,
  keyBase: string,
  lang: 'en' | 'zh',
  onInternal: (path: string) => void,
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const re =
    /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)|(\/(?:about|services|pricing|testimonials|faq|policy-updates|pathway-finder|contact|book-consultation)\b)/g;
  // Internal links render as a tappable pill so they read like an in-app option,
  // not a URL. External links stay a normal underlined link.
  const pillClass =
    'mx-0.5 inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-0.5 align-middle text-sm font-medium text-teal-700 transition-colors hover:bg-teal-100 cursor-pointer';
  const extClass =
    'font-medium text-teal-600 underline underline-offset-2 hover:text-teal-700 cursor-pointer break-words';
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    let label: string;
    let target: string;
    if (m[1] != null && m[2] != null) {
      label = m[1];
      target = m[2];
    } else {
      target = (m[3] ?? m[4] ?? '').replace(/[).,!?；。，、]+$/, '');
      label = target;
    }
    const res = resolveLink(target);
    if (res.internal) {
      const path = res.internal;
      // A bare path (or a label that is just the path) becomes a friendly name.
      const display = /^\/\S*$/.test(label.trim()) ? friendlyLabel(path, lang) : label;
      nodes.push(
        <button key={`${keyBase}-${i}`} type="button" onClick={() => onInternal(path)} className={pillClass}>
          {display}
          <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
        </button>,
      );
    } else if (res.external) {
      // Show the real domain beside a non-URL label so an allowlisted link can never be
      // dressed up under a misleading name (label spoofing). Skipped when the label IS
      // the URL or already names the domain.
      const trimmed = label.trim();
      const showHost =
        !!res.host && trimmed !== res.external && !trimmed.toLowerCase().includes(res.host);
      nodes.push(
        <a key={`${keyBase}-${i}`} href={res.external} target="_blank" rel="noopener noreferrer" className={extClass}>
          {label}{showHost ? ` (${res.host})` : ''}
        </a>,
      );
    } else {
      // Not internal, not an allowlisted external domain → plain text (no <a>). For a
      // markdown link this drops the (possibly deceptive) label and shows the raw target,
      // so the true destination is never hidden behind a trustworthy-looking name.
      nodes.push(target);
    }
    last = re.lastIndex;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function renderRich(
  text: string,
  keyBase: string,
  lang: 'en' | 'zh',
  onInternal: (path: string) => void,
): React.ReactNode[] {
  return text.split(/(\*\*[^*]+?\*\*)/g).map((seg, i) => {
    const bold = /^\*\*([^*]+?)\*\*$/.exec(seg);
    if (bold) {
      return <strong key={`${keyBase}-b${i}`}>{renderInline(bold[1], `${keyBase}-b${i}`, lang, onInternal)}</strong>;
    }
    return <span key={`${keyBase}-s${i}`}>{renderInline(seg, `${keyBase}-s${i}`, lang, onInternal)}</span>;
  });
}

// Persist the conversation for the browser tab so navigating between pages (or a
// reload) never wipes Hank's history. sessionStorage = per-tab and auto-cleared
// when the tab closes — the right scope for a support chat (no stale conversation
// lingering on a shared computer).
const CHAT_STORAGE_KEY = 'tf-hank-chat-v1';
const MAX_STORED = 50;

function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string',
      );
    }
  } catch {
    /* private mode / corrupt value — start fresh */
  }
  return [];
}

// Programmatic open+prefill bridge (T76; extended T79). Any part of the app (e.g.
// the intake form's 翻译帮助 or ask-Hank affordance) can open Hank with the composer
// pre-seeded — without prop-drilling through the app-wide mount. A window
// CustomEvent keeps the two sides decoupled; the mounted panel listens (below).
// No-op outside the browser (SSR / the renderToString test env), so callers stay
// render-safe.
//
// T79: an optional STRUCTURED context ({ fieldId, pathwayId }) rides the event and
// is attached to the NEXT send's request payload — NEVER parsed from message text.
// The edge function validates fieldId against the glossary allowlist and injects
// the one matching reference row; an unknown/absent id changes nothing.
const HANK_OPEN_EVENT = 'hank:open-prefill';

export interface HankContext {
  fieldId?: string;
  pathwayId?: string;
  // T81 — consultant mode: an admin surface (or a future in-face caller) can arm the
  // route + optional caseId. The edge function still VERIFIES the auth token server-side
  // before any of this unlocks; caseId is only an identifier (state is DB-resolved).
  route?: string;
  caseId?: string;
}

export function openHankChat(prefill: string, context?: HankContext): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(HANK_OPEN_EVENT, { detail: { prefill, context } }));
}

// ── T81 — consultant recipe actions (带我去 buttons) ──
// A resolved, presentational descriptor: the container turns the fn's recipeId[] into
// these via the T80 palette plumbing (resolveRoute / pathwayState / tr), then renders the
// buttons. Actions are CODE ONLY — navigate + launch a tour — NEVER a case mutation
// (red line ②: Hank points, the consultant clicks; nothing here changes a case).
export interface RecipeAction {
  id: string;
  label: string;
  /** The resolved href for 带我去, or null when the recipe needs a case / is inactive. */
  href: string | null;
  /** Which honest note to show when href is null. */
  disabled?: 'needsCase' | 'inactive';
  /** A tour id for the optional 演示 (show-me) launch. */
  tourId?: string;
}

/**
 * Resolve the fn's recipeId[] into render descriptors using the (dynamically loaded) T80
 * palette core — the SAME resolveRoute / pathwayState the command palette uses, so a chat
 * 带我去 lands exactly where the palette's does. Unknown ids (atlas drift) are skipped, so
 * a stale id never renders a broken button. Pure — no side effects.
 */
export function resolveRecipeActions(
  recipeIds: string[],
  core: PaletteCore,
  lang: 'en' | 'zh',
  caseId?: string,
): RecipeAction[] {
  const byId = new Map<string, AtlasRecipe>(core.RECIPES.map((r) => [r.id, r]));
  const out: RecipeAction[] = [];
  for (const id of recipeIds) {
    const r = byId.get(id);
    if (!r) continue;
    const inactive = core.pathwayState(r) === 'inactive';
    const { href, needsCase } = core.resolveRoute(r, { caseId });
    out.push({
      id,
      label: core.tr(r.intent, lang),
      href: inactive || needsCase ? null : href,
      disabled: inactive ? 'inactive' : needsCase ? 'needsCase' : undefined,
      tourId: !inactive && r.tourId ? r.tourId : undefined,
    });
  }
  return out;
}

/** The button row under a consultant answer. Presentational (no atlas import, no engine) —
 *  a 带我去 <Link> (navigation proof = its resolved href, testable under a MemoryRouter),
 *  an honest disabled note when a case/activation is missing, and an optional 演示 button
 *  that calls onDemo (the container wires the tour engine). */
export function ConsultantRecipeActions({
  actions,
  labels,
  onNavigate,
  onDemo,
}: {
  actions: RecipeAction[];
  labels: { goto: string; demo: string; needsCase: string; notActive: string };
  onNavigate: () => void;
  onDemo: (tourId: string) => void;
}) {
  if (actions.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-consultant-actions>
      {actions.map((a) => (
        <span key={a.id} data-action={a.id} className="inline-flex items-center gap-1">
          {a.href ? (
            <Link
              to={a.href}
              onClick={onNavigate}
              data-goto={a.id}
              className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-teal-700"
            >
              {a.label} · {labels.goto}
              <i className="ri-arrow-right-line text-xs" aria-hidden="true"></i>
            </Link>
          ) : (
            <span
              data-disabled={a.disabled}
              className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-400"
            >
              {a.label} · {a.disabled === 'inactive' ? labels.notActive : labels.needsCase}
            </span>
          )}
          {a.tourId && (
            <button
              type="button"
              data-demo={a.id}
              onClick={() => onDemo(a.tourId as string)}
              className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 cursor-pointer"
            >
              {labels.demo}
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

// T223 hub 收口:launcher='external' 时不渲染自带浮钮(面板与 openHankChat 事件照常),
// 由 App 层的 v2 ChatFAB 充当唯一入口——否则两钮同位叠放(T220 交接注记的双钮问题)。
export default function ChatAssistant({ launcher = 'own' }: { launcher?: 'own' | 'external' } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('zh') ? 'zh' : 'en';
  const t = STRINGS[lang];

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadStoredMessages());

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenResolveRef = useRef<((t: string) => void) | null>(null);
  const hasExecutedRef = useRef(false);
  const sendingRef = useRef(false);
  // Structured field context armed by an ask-Hank affordance (T79); consumed by the
  // next send, then cleared (one-shot — a later free-form question carries none).
  const pendingContextRef = useRef<HankContext | null>(null);

  // T81 — consultant mode: active on an admin surface with a live Supabase session. This
  // detection is UX only (shows 顾问模式, sends { route } + the token); the edge function
  // VERIFIES the token server-side and is the hard wall — a public visitor has no session
  // here and, even if they forged one, the server refuses to unlock without a staff token.
  const isAdminSurface = location.pathname.startsWith('/admin');
  const [hasSession, setHasSession] = useState(false);
  const consultantMode = isAdminSurface && hasSession;
  // The T80 palette core, loaded lazily ONLY in consultant mode (dynamic import → the
  // consultant atlas stays out of the public/eager bundle) to resolve recipeId[] → buttons.
  const [paletteCore, setPaletteCore] = useState<PaletteCore | null>(null);
  const tourEngineRef = useRef<{ destroy: () => void } | null>(null);

  // Seed the greeting (display-only — it's stripped before hitting the API) and
  // refresh it if the user switches language before sending anything.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0 || (prev.length === 1 && prev[0].role === 'assistant')) {
        return [{ role: 'assistant', content: t.greeting }];
      }
      return prev;
    });
  }, [t.greeting]);

  // Persist the conversation per tab so navigation / reload never wipes it. Skip
  // the lone greeting so a brand-new tab still greets in the current language.
  useEffect(() => {
    try {
      if (messages.length > 1) {
        sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED)));
      }
    } catch {
      /* ignore quota / private mode */
    }
  }, [messages]);

  // Keep the latest message in view.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // T81 — track whether a Supabase session exists (a consultant/admin is logged in).
  // Public visitors have none, so consultant mode never turns on for them client-side;
  // the server-side token gate is the hard wall regardless of what the client believes.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Lazy-load the T80 palette core the first time consultant mode is active — a dynamic
  // import keeps the consultant atlas out of the public/eager bundle (admin-only, like
  // the palette itself). recipeId[] can't be resolved into buttons until this lands.
  useEffect(() => {
    if (!consultantMode || paletteCore) return;
    let active = true;
    void import('./admin-case/commandPaletteCore').then((mod) => {
      if (active) setPaletteCore(mod);
    });
    return () => {
      active = false;
    };
  }, [consultantMode, paletteCore]);

  // Tear any running demo down on unmount.
  useEffect(
    () => () => {
      tourEngineRef.current?.destroy();
      tourEngineRef.current = null;
    },
    [],
  );

  // 演示: launch a recipe's tour through the T80 engine's PUBLIC api (show-me auto-plays
  // and changes nothing — navigation/spotlight only, never a case mutation). Dynamically
  // imported so the tour engine (driver.js) stays out of the public bundle.
  const handleRecipeDemo = async (tourId: string) => {
    const [{ getTour }, { TourEngine }] = await Promise.all([
      import('./tour/tours'),
      import('./tour/tourEngine'),
    ]);
    const tour = getTour(tourId);
    if (!tour) return;
    setOpen(false);
    tourEngineRef.current?.destroy();
    const engine = new TourEngine(tour, {
      mode: 'show-me',
      lang,
      onDone: () => {
        tourEngineRef.current = null;
      },
      onExit: () => {
        tourEngineRef.current = null;
      },
    });
    tourEngineRef.current = engine;
    engine.start();
  };

  // Programmatic open+prefill (T76): open the panel with the composer pre-seeded
  // (e.g. the intake form's 翻译帮助 affordance passing the client's own field text
  // + the bilingual translate prompt). The text arrives only because the user
  // tapped the affordance — this listener never sends, it only fills the composer
  // so the user reviews and presses send themselves.
  useEffect(() => {
    const onOpenPrefill = (e: Event) => {
      const detail = (e as CustomEvent).detail as { prefill?: unknown; context?: unknown } | undefined;
      const prefill = typeof detail?.prefill === 'string' ? detail.prefill : '';
      const ctx = detail?.context && typeof detail.context === 'object'
        ? (detail.context as HankContext)
        : null;
      setOpen(true);
      if (prefill) setInput(prefill);
      pendingContextRef.current = ctx;   // armed for the next send (one-shot)
      requestAnimationFrame(() => inputRef.current?.focus());
    };
    window.addEventListener(HANK_OPEN_EVENT, onOpenPrefill);
    return () => window.removeEventListener(HANK_OPEN_EVENT, onOpenPrefill);
  }, []);

  // Load Cloudflare Turnstile and render an invisible, on-demand widget when the
  // chat first opens. A fresh single-use token is fetched before each message.
  useEffect(() => {
    if (!open) return;
    const renderWidget = () => {
      const ts = window.turnstile;
      if (!ts || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = ts.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        // "Invisible" is a property of the sitekey configured in the Cloudflare
        // dashboard (NOT a render option) — the sitekey must be created as widget
        // type "Invisible". We trigger it on demand via execute() below.
        execution: 'execute',
        callback: (token: string) => tokenResolveRef.current?.(token),
        'error-callback': () => tokenResolveRef.current?.(''),
        'expired-callback': () => tokenResolveRef.current?.(''),
      });
    };
    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById('cf-turnstile-script')) {
      const s = document.createElement('script');
      s.id = 'cf-turnstile-script';
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      s.onerror = () => console.error('[turnstile] script failed to load');
      document.head.appendChild(s);
    }
    return () => {
      tokenResolveRef.current = null;
    };
  }, [open]);

  // Fetch a fresh single-use Turnstile token (resolves '' if unavailable, e.g.
  // the script failed to load — the server only enforces when its secret is set).
  const getTurnstileToken = (): Promise<string> => {
    const ts = window.turnstile;
    const wid = widgetIdRef.current;
    if (!ts || !wid) return Promise.resolve('');
    return new Promise((resolve) => {
      let done = false;
      const finish = (tok: string) => {
        if (done) return;
        done = true;
        tokenResolveRef.current = null;
        resolve(tok);
      };
      tokenResolveRef.current = finish;
      try {
        // Single-use tokens: execute() once on the first send; reset()+execute()
        // for a fresh token on later sends. (reset() before the first execute()
        // can race the widget's init on some Turnstile versions.)
        if (hasExecutedRef.current) ts.reset(wid);
        ts.execute(wid);
        hasExecutedRef.current = true;
      } catch {
        finish('');
      }
      window.setTimeout(() => finish(''), 12000);
    });
  };

  const sendMessage = async () => {
    const text = input.trim();
    // Synchronous in-flight guard (the `loading` state is a stale closure under
    // rapid Enter presses) — serializes sends so Turnstile tokens can't cross.
    if (!text || loading || sendingRef.current) return;
    sendingRef.current = true;

    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);

    // Consume any armed field context (one-shot, T79). On an admin surface with a session,
    // fall back to the consultant context { route } so the fn can — AFTER verifying the
    // token server-side — ground the answer. An armed context (e.g. a future in-face caller
    // passing { route, caseId }) wins.
    const contextForSend: HankContext | undefined =
      pendingContextRef.current ?? (consultantMode ? { route: location.pathname } : undefined);
    pendingContextRef.current = null;

    try {
      const turnstileToken = await getTurnstileToken();
      // Consultant mode: attach the caller's Supabase access token so the fn can VERIFY it
      // server-side (the unlock is gated on that verification, never on this client state).
      let headers: Record<string, string> | undefined;
      if (consultantMode) {
        const { data: sess } = await supabase.auth.getSession();
        const token = sess.session?.access_token;
        if (token) headers = { Authorization: `Bearer ${token}` };
      }
      const { data, error } = await supabase.functions.invoke('chat-assistant', {
        body: {
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          turnstileToken,
          ...(contextForSend ? { context: contextForSend } : {}),
        },
        ...(headers ? { headers } : {}),
      });

      if (error) throw error;
      const reply = (data?.reply as string) || t.error;
      // Consultant mode only: the fn returns the selected recipe ids for the 带我去 buttons.
      const recipeIds = Array.isArray(data?.recipeIds)
        ? (data.recipeIds as unknown[]).filter((x): x is string => typeof x === 'string')
        : undefined;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply, ...(recipeIds && recipeIds.length ? { recipeIds } : {}) },
      ]);
    } catch (err) {
      console.error('[chat-assistant] error:', err);
      setMessages((prev) => [...prev, { role: 'assistant', content: t.error }]);
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const goToBooking = () => {
    setOpen(false);
    navigate(localizeInternalPath('/book-consultation', location.pathname));
  };

  // Clicking an in-chat link to one of our pages navigates in-app (no copy/paste)
  // and closes the panel so the customer sees the page.
  const handleInternalNav = (path: string) => {
    setOpen(false);
    navigate(localizeInternalPath(path, location.pathname));
  };

  return (
    <>
      {/* Cloudflare Turnstile — invisible bot-protection widget (always mounted) */}
      <div ref={turnstileRef} className="hidden" />

      {/* Floating launcher button — T95 skin pass only (§1b chat-bubble): the
          glow shadow becomes the token elevation pair; pill shape (a mark)
          stays rounded-full. Logic, aria, and copy untouched.
          [R57] Below `sm` the launcher is a plain 48×48 icon circle: the label
          was already `hidden sm:inline`, but the pill kept its px-5/py-3.5
          padding, so it floated over the phone viewport at 60×56. The full
          `aria-label` is unchanged, so the button reads identically to assistive
          tech at every width; at `sm` and up the capsule is restored exactly. */}
      {!open && launcher === 'own' && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t.open}
          className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-teal-600 text-white shadow-card transition-all duration-brand-slow ease-out hover:bg-teal-700 hover:shadow-card-hover cursor-pointer sm:h-auto sm:w-auto sm:justify-start sm:gap-2 sm:px-5 sm:py-3.5"
        >
          <i className="ri-message-3-line text-xl"></i>
          <span className="hidden sm:inline font-medium whitespace-nowrap">{t.open}</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 flex h-[min(70vh,560px)] w-[calc(100vw-2.5rem)] max-w-[400px] flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-card-hover">
          {/* Header */}
          <div className="flex items-center justify-between bg-teal-600 px-4 py-3 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <i className="ri-customer-service-2-line text-lg"></i>
              </div>
              <div className="leading-tight">
                <p className="flex items-center gap-1.5 font-semibold">
                  {t.title}
                  {consultantMode && (
                    <span
                      data-consultant-badge
                      className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-medium"
                    >
                      {t.consultantBadge}
                    </span>
                  )}
                </p>
                <p className="text-xs text-teal-50">
                  {consultantMode ? t.consultantSubtitle : t.subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t.close}
              className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/20 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-gray-50 px-4 py-4">
            {messages.map((m, i) => {
              // Consultant mode only: resolve this answer's recipe ids into 带我去 buttons
              // via the (lazily loaded) T80 palette core. Empty for public turns / before
              // the core loads / off an admin surface.
              const actions =
                consultantMode && paletteCore && m.role === 'assistant' && m.recipeIds?.length
                  ? resolveRecipeActions(m.recipeIds, paletteCore, lang)
                  : [];
              return (
                <div
                  key={i}
                  className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div
                    // data-hank-reply: the STABLE test anchor the e2e copilot/boundary
                    // suites (and their fixture) key on — the fixture carried it but the
                    // real widget didn't, so the real prod leg could never pass (found
                    // at the first credentialed run, log 173). Attribute only; no styling.
                    {...(m.role === 'assistant' ? { 'data-hank-reply': '' } : {})}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === 'user'
                        ? 'rounded-br-sm bg-teal-600 text-white'
                        : 'rounded-bl-sm bg-white text-gray-800 shadow-sm ring-1 ring-gray-100'
                    }`}
                  >
                    {m.role === 'assistant'
                      ? renderRich(m.content, `m${i}`, lang, handleInternalNav)
                      : m.content}
                  </div>
                  {actions.length > 0 && (
                    <ConsultantRecipeActions
                      actions={actions}
                      labels={{ goto: t.goto, demo: t.demo, needsCase: t.needsCase, notActive: t.notActive }}
                      onNavigate={() => setOpen(false)}
                      onDemo={handleRecipeDemo}
                    />
                  )}
                </div>
              );
            })}

            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400 [animation-delay:-0.3s]"></span>
                  <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400 [animation-delay:-0.15s]"></span>
                  <span className="h-2 w-2 animate-bounce rounded-full bg-teal-400"></span>
                </div>
              </div>
            )}
          </div>

          {/* Booking CTA */}
          <button
            onClick={goToBooking}
            className="flex items-center justify-center gap-2 border-t border-gray-100 bg-white px-4 py-2.5 text-sm font-medium text-teal-700 transition-colors hover:bg-teal-50 cursor-pointer"
          >
            <i className="ri-calendar-check-line"></i>
            {t.book}
          </button>

          {/* Composer */}
          <div className="border-t border-gray-100 bg-white px-3 py-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder={t.placeholder}
                className="max-h-28 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                aria-label={t.send}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                <i className="ri-send-plane-fill text-lg"></i>
              </button>
            </div>
            <p className="mt-1.5 text-center text-[11px] text-gray-400">{t.disclaimer}</p>
          </div>
        </div>
      )}
    </>
  );
}
